import React, { useEffect, useState } from "react";
import {
  SearchIcon,
  IncomingIcon,
  CompletedIcon,
  RejectedIcon,
  PendingIcon,
} from "../../assets/svgs/Icons";
import { FiFilter } from "react-icons/fi";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Tab,
  Table,
  TableCaption,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  Link,
  Select,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  getAllApplications,
  getIncomingApplications,
  getRcExtensionApplications,
  getRcRenewalApplications,
  postAssignToMe,
} from "./CertifierSlice";
import {
  Column,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  sortingFns,
  getSortedRowModel,
  FilterFn,
  SortingFn,
  ColumnDef,
  flexRender,
  FilterFns,
  createColumnHelper,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import moment from "moment/moment";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import isAllowed from "../../routing/isAllowed";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import Card from "./add-card";

function RejectedApplications() {
  const [status, setStatus] = useState("idle");
  const [columnFilters, setColumnFilters] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const applicationStatus = "REJECT";
  const dispatch = useDispatch();
  const {
    incomingApplications,
    rcExtensionApplications,
    rcRenewalApplications,
  } = useSelector((state) => state.certifier);
  const { isLoading } = useSelector((state) => state.certifier);
  const [activeTab, setActiveTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const navigate = useNavigate();

  const getList = async () => {
    try {
      setStatus("pending");
      const result = dispatch(getIncomingApplications(applicationStatus));
      unwrapResult(result);
      setStatus("success");
    } catch (error) {
      setStatus("failed");
      window.alert(error);
    }
  };

  const getRCExtensionList = async () => {
    try {
      setStatus("pending");
      const result = dispatch(getRcExtensionApplications(applicationStatus));
      unwrapResult(result);
      setStatus("success");
    } catch (error) {
      setStatus("failed");
      window.alert(error);
    }
  };

  const getRCRenewalList = async () => {
    try {
      setStatus("pending");
      const result = dispatch(getRcRenewalApplications(applicationStatus));
      unwrapResult(result);
      setStatus("success");
    } catch (error) {
      setStatus("failed");
      window.alert(error);
    }
  };

  const updateIncomingData = () => {
    const updatedData = incomingApplications?.map((d, index) => {
      return {
        ...d,
        srNo: index + 1,
        actions: "actions",
        dateTime: `${moment(d?.createdAt).format("DD-MM-YYYY")}`,
        assign_action: "assign_action",
        unitProcesses: d?.applicationForm_process?.map((data) => {
          return data?.name;
        }),
      };
    });

    setUpdatedData(updatedData);
  };

  useEffect(() => {
    const mapData = (data) =>
      data?.map((item, index) => ({
        srNo: index + 1,
        dateTime: moment(item.createdAt).format("DD-MM-YYYY"),
        id: item.id || item._id,
        name: item.name || item.scop_company?.name,
        companyScale: item.companyScale || item.scop_company?.companyScale,
        address:
          item.address?.raw_address || item?.scop_company?.address?.raw_address,
        assignedTo:
          item?.company_assignedTo?.name || item?.scop_assignedTo?.name,
        rejectedBy: item?.company_rejectedBy?.name,
        actions: "actions",
      }));

    switch (activeTab) {
      case 0:
        setTableData(mapData(incomingApplications));
        break;
      case 1:
        setTableData(mapData(rcExtensionApplications));
        break;
      case 2:
        setTableData(mapData(rcRenewalApplications));
        break;
      default:
        setTableData([]);
    }
  }, [
    activeTab,
    incomingApplications,
    rcExtensionApplications,
    rcRenewalApplications,
  ]);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("srNo", {
      cell: (info) => info.getValue(),
      header: "srNo",
    }),
    columnHelper.accessor("dateTime", {
      cell: (info) => info.getValue(),
      header: "Date",
      sortType: (a, b) => {
        var a1 = new Date(a).getTime();
        var b1 = new Date(b).getTime();
        if (a1 < b1) return 1;
        else if (a1 > b1) return -1;
        else return 0;
      },
    }),
    columnHelper.accessor("id", {
      cell: (info) => info.getValue(),
      header: "Application Number",
    }),
    columnHelper.accessor("name", {
      cell: (info) => info.getValue() || "N/A",
      header: "Company Name",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("companyScale", {
      cell: (info) => info.getValue() || "N/A",
      header: "Scale",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("address", {
      cell: (info) => info.getValue() || "N/A",
      header: "Location",
      // meta: {
      //   isNumeric: true,
      // },
    }),
    columnHelper.accessor("rejectedBy", {
      cell: (info) => info.getValue() || "N/A",
      header: "Rejected By",
    }),

    columnHelper.accessor("remarks", {
      cell: (info) => {
        const remarks = info.getValue();
        const lastRemark =
          remarks && remarks.length > 0 && remarks[remarks.length - 1];
        const comment = lastRemark && lastRemark.comment;
        return comment !== undefined && comment !== null ? comment : "N/A";
      },
      header: "Remarks",
    }),

    columnHelper.accessor("actions", {
      Cell: ({ cell }) => console.log(),
      header: "Actions",
      // meta: {
      //   isNumeric: true,
      // },
    }),
  ];

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value);
    // Store the itemRank info
    addMeta({
      itemRank,
    });
    // Return if the item should be filtered in/out
    return itemRank.passed;
  };

  const table = useReactTable({
    data: tableData || [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // getFacetedRowModel: getFacetedRowModel(),
    // getFacetedUniqueValues: getFacetedUniqueValues(),
    // getFacetedMinMaxValues: getFacetedMinMaxValues(),
    // Disable "Creating Table Instance" message
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  useEffect(() => {
    getList(applicationStatus);
    getRCExtensionList(applicationStatus);
    getRCRenewalList(applicationStatus);
  }, []);

  // useEffect(() => {
  //   updateIncomingData();
  // }, [incomingApplications]);

  return (
    <Box>
      <Tabs onChange={(index) => setActiveTab(index)}>
        <TabList>
          <Tab>RC Applications</Tab>
          <Tab>RC Extensions</Tab>
          <Tab>RC Renewals</Tab>
        </TabList>
      </Tabs>
      {tableData.length > 0 ? (
        <>
          <Box marginTop={"1rem"}>
            {/* <Flex mb="1rem" flexDirection={"row"}>
              <Text> Certifier/ </Text>
              <Text fontWeight={"bold"}>Rejected Applications</Text>
            </Flex> */}
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <InputGroup w={{ base: "100%", md: "auto" }}>
                  <InputLeftElement
                    height={"100%"}
                    pointerEvents="none"
                    children={<SearchIcon color="#B8DEF8" />}
                  />
                  <Input
                    type="text"
                    color={"#464C64"}
                    bg={"#fff"}
                    w={{ base: "100%", md: "500px" }}
                    h={"48px"}
                    value={globalFilter ?? ""}
                    placeholder="Search All Results"
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    fontSize={"14px"}
                    borderRadius={"10px"}
                    _placeholder={{
                      opacity: 1,
                      color: "#464C64",
                      fontSize: "14px",
                    }}
                  />
                </InputGroup>
              </Box>
              <Box>
                <Box
                  display={"flex"}
                  justifyContent="space-between"
                  flexDirection={{ base: "column", md: "row" }}
                  gap={{ base: "1rem", md: "0" }}
                >
                  <Box>
                    <Box
                      display={"flex"}
                      alignItems="center"
                      marginBottom={"10px"}
                      flexDirection={{ base: "column", md: "row" }}
                      gap={{ base: "0.5rem", md: "0" }}
                    >
                      <Box
                        as="span"
                        display="flex"
                        alignItems="center"
                        gap="5px"
                        marginRight={"10px"}
                        width={{ base: "100%", md: "auto" }}
                      >
                        <Box as="span" width={{ base: "auto", md: "30%" }}>
                          {" "}
                          Go to page:{" "}
                        </Box>
                        <Input
                          w={{ base: "100%", md: "60%" }}
                          type="number"
                          color={"#464C64"}
                          bg={"#fff"}
                          defaultValue={
                            table.getState().pagination.pageIndex + 1
                          }
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            table.setPageIndex(page);
                          }}
                        />
                      </Box>
                      <Box>
                        <Select
                          bg="#fff"
                          value={table.getState().pagination.pageSize}
                          onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                          }}
                        >
                          {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                              Show {pageSize}
                            </option>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Flex>
          </Box>
          <Box marginTop={"2rem"}>
            <Box
              as="span"
              display="flex"
              alignItems="center"
              marginBottom={"10px"}
              gap="5px"
            >
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </Box>
            <Tabs size="md" variant="enclosed">
              {isMobile ? (
                <Box>
                  {isLoading ? (
                    <Box>
                      {[1, 2, 3, 4].map((item) => (
                        <Box key={item} mb={4}>
                          <Skeleton height="200px" fadeDuration={1} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box>
                      {table.getRowModel()?.rows?.map((row) => (
                        <Card
                          key={row?.id}
                          data={row?.original}
                          activeTab={activeTab}
                          navigate={navigate}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <TableContainer className="overflow-style" overflowX={"scroll"}>
                  <Table bg="brand.white" borderRadius="15px">
                    <Thead fontStyle={"normal"}>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                            const meta = header.column.columnDef.meta;
                            return (
                              <Th
                                key={header.id}
                                color="brand.blue"
                                textTransform="capitalize"
                                onClick={header.column.getToggleSortingHandler()}
                                isNumeric={meta?.isNumeric}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                <Box as="span" pl="1rem">
                                  {header.column.getIsSorted() ? (
                                    header.column.getIsSorted() === "desc" ? (
                                      <TriangleDownIcon
                                        aria-label="sorted descending"
                                        color={"#3958DC"}
                                        size="20"
                                      />
                                    ) : (
                                      <TriangleUpIcon
                                        aria-label="sorted ascending"
                                        color={"#3958DC"}
                                        size="20"
                                      />
                                    )
                                  ) : null}
                                </Box>
                              </Th>
                            );
                          })}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {isLoading ? (
                        <>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                            <Td>
                              <Skeleton height="20px" fadeDuration={1} />
                            </Td>
                          </Tr>
                        </>
                      ) : (
                        <>
                          {table.getRowModel()?.rows?.map((row) => (
                            <Tr key={row?.id}>
                              {row.getVisibleCells().map((cell) => {
                                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                                const meta = cell?.column?.columnDef?.meta;
                                return (
                                  <Td
                                    key={cell?.id}
                                    isNumeric={meta?.isNumeric}
                                    fontWeight={500}
                                    fontSize="sm"
                                    style={{
                                      maxWidth: "250px",
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    {cell?.renderValue() === "actions" ? (
                                      <Link
                                        color={"#3958DC"}
                                        fontWeight="800"
                                        onClick={() => {
                                          const id = encodeURIComponent(
                                            row.original.id
                                          );
                                          if (activeTab === 2) {
                                            navigate(
                                              `./rc-form-certifier-renew/${id}`
                                            );
                                          } else if (activeTab === 1) {
                                            navigate(
                                              `./rc-form-certifier-extension/${id}`
                                            );
                                          } else {
                                            navigate(
                                              `./rc-form-certifier/${id}`
                                            );
                                          }
                                        }}
                                      >
                                        View Details
                                      </Link>
                                    ) : (
                                      flexRender(
                                        cell?.column?.columnDef?.cell,
                                        cell?.getContext()
                                      )
                                    )}
                                  </Td>
                                );
                              })}
                            </Tr>
                          ))}
                        </>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              <Box
                display={"flex"}
                justifyContent="center"
                marginTop={"1rem"}
                marginBottom="2rem"
              >
                <Box display={"flex"} gap="2rem">
                  <Button
                    className="border rounded p-1"
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <AiOutlineArrowLeft color="#141D43" size={18} />
                  </Button>
                  <Button
                    className="border rounded p-1"
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    <AiOutlineArrowRight color="#141D43" size={18} />
                  </Button>
                </Box>
              </Box>
            </Tabs>
          </Box>
        </>
      ) : (
        <Box
          display={"flex"}
          alignItems="center"
          marginTop={"8rem"}
          justifyContent="center"
        >
          <Text fontSize={"25px"} fontWeight={"bold"} textAlign="center">
            There are no rejected applications yet
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default isAllowed(RejectedApplications);
